/**
 * Calculation utilities for expanded row data
 */
import type { TemplateConfigField, FieldValueResult } from './types'
import { CAPACITY_COMMITMENT_TEMPLATES } from './constants'

/**
 * Config types that should use commitment_value directly (not capacity * commitment formula)
 */
const DIRECT_COMMITMENT_TYPES = ['base-config']

/**
 * Calculate config value based on config type and data
 * Priority:
 * 1. base-config type: use commitment_value directly
 * 2. Has capacity_value: use (commitment_value || 1) * capacity_value
 * 3. Fallback: use commitment_value
 */
export const getConfigQuota = (
    config: any,
    _serviceTemplate?: string
): number => {
    // For base-config type, use commitment_value directly
    if (DIRECT_COMMITMENT_TYPES.includes(config.type?.toLowerCase())) {
        return config.commitment_value || 0
    }

    // If has capacity_value, use formula (regardless of template)
    if (config.capacity_value) {
        const commitment = config.commitment_value || 1
        return commitment * (config.capacity_value || 0)
    }

    return config.commitment_value || 0
}

/**
 * Check if a value should be displayed (not empty, not 0, not falsy)
 * Use this everywhere to ensure consistent behavior
 */
export const shouldDisplayValue = (value: any): boolean => {
    if (value === null || value === undefined) return false
    if (value === 0 || value === '0') return false
    if (value === '' || value === false) return false
    if (typeof value === 'number' && isNaN(value)) return false
    return true
}

/**
 * Check if a config has a displayable value
 */
export const hasDisplayableValue = (
    config: any,
    serviceTemplate?: string
): boolean => {
    const value = getConfigQuota(config, serviceTemplate)
    return shouldDisplayValue(value)
}

/**
 * Calculate config value for a field from all configs (main + addons)
 */
export const calculateFieldValue = (
    allConfigs: any[],
    field: TemplateConfigField,
    serviceTemplate?: string
): FieldValueResult => {
    const matchingConfigs = allConfigs.filter((c) =>
        field.names.includes(c.name?.toLowerCase())
    )

    if (matchingConfigs.length === 0) {
        return { value: 0, unit: field.unit || '' }
    }

    const isCapacityCommitment = CAPACITY_COMMITMENT_TEMPLATES.some((t) =>
        serviceTemplate?.includes(t)
    )

    let totalValue = 0
    let unit = field.unit || ''
    const details: FieldValueResult['details'] = []
    let modelName: string | undefined
    const formulaParts: FieldValueResult['formulaParts'] = []

    matchingConfigs.forEach((config) => {
        const configValue = getConfigQuota(config, serviceTemplate)
        totalValue += configValue
        unit = unit || config.unit || config.capacity_unit || ''

        // For server/processor, capture model_name
        if (field.key === 'server' && config.model_name) {
            modelName = config.model_name
        }

        // Capture formula parts for VBM/Colocation templates
        if (isCapacityCommitment && config.capacity_value) {
            formulaParts.push({
                modelName: config.model_name,
                capacityValue: config.capacity_value || 0,
                commitmentValue: config.commitment_value || 1,
            })
        }

        if (config.brand_name || config.model_name) {
            details.push({
                brand: config.brand_name,
                model: config.model_name,
                value: configValue,
                isAddon: config._isAddon,
            })
        }
    })

    return {
        value: totalValue,
        unit,
        details: details.length > 0 ? details : undefined,
        modelName,
        formulaParts: formulaParts.length > 0 ? formulaParts : undefined,
    }
}

/**
 * Calculate price based on pricing model
 */
export const calculateRowPrice = (
    pricingModel: string,
    configoptions: any[],
    directPrice: number
): number => {
    if (pricingModel?.toLowerCase() === 'bundle') {
        return (configoptions || []).reduce((sum: number, config: any) => {
            const commitment = config.commitment_value || 1
            const pricePerUnit = config.commitment_price_per_unit || 0
            return sum + commitment * pricePerUnit
        }, 0)
    }
    return directPrice || 0
}
